import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../store';

// Re-defining interfaces here, assuming they might be shared across the app
// If they are only used for tracking, this is the right place.
export interface TrackingStatus {
    status: string;
    date: string;
    time: string;
    location: string;
    desc: string;
}

export interface TrackingInfo {
    number: string;
    orderId: string;
    bookingDate: string;
    bookingType: string;
    fromInfo: { name: string; city: string; };
    toInfo: { name: string; city: string; };
    statuses: TrackingStatus[];
}

interface TrackingState {
    trackings: TrackingInfo[];
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: TrackingState = {
    trackings: [],
    loading: 'idle',
    error: null,
};

// Async thunk for fetching tracking data
export const fetchTrackingsByNumbers = createAsyncThunk<TrackingInfo[], string[], { rejectValue: string }>(
    'tracking/fetchByNumbers',
    async (trackingNumbers, { rejectWithValue }) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(`${backendUrl}/track`, {
                tracking_numbers: trackingNumbers,
            });
            // Map API response to TrackingInfo[]
            const apiData = response.data.data;
            const mapped: TrackingInfo[] = apiData.map((item: any) => {
                if (item.status === 'not_found') {
                    return {
                        number: item.tracking_number,
                        orderId: '',
                        bookingDate: '',
                        bookingType: '',
                        fromInfo: { name: '', city: '' },
                        toInfo: { name: '', city: '' },
                        statuses: [
                            {
                                status: 'Not Found',
                                date: '',
                                time: '',
                                location: '',
                                desc: item.message || 'Consignment not found',
                            },
                        ],
                    };
                }
                // Map found consignment
                const fromInfo = item.consigner && item.origin ? {
                    name: item.consigner.name || '',
                    city: item.origin.city || ''
                } : { name: '', city: '' };
                const toInfo = item.consignee && item.destination ? {
                    name: item.consignee.name || '',
                    city: item.destination.city || ''
                } : { name: '', city: '' };
                const statuses = Array.isArray(item.tracking_history)
                    ? item.tracking_history.map((h: any) => {
                        let date = '', time = '';
                        if (h.timestamp) {
                            const [d, t] = h.timestamp.split(' ');
                            date = d || '';
                            time = t || '';
                        }
                        return {
                            status: h.status?.name || '',
                            date,
                            time,
                            location: item.destination?.city || '',
                            desc: h.description || '',
                        };
                    })
                    : [];
                return {
                    number: item.tracking_number,
                    orderId: '', // Not present in API
                    bookingDate: item.created_at || '',
                    bookingType: item.booking_type || '',
                    fromInfo,
                    toInfo,
                    statuses,
                };
            });
            return mapped;
        } catch (err: any) {
            if (axios.isAxiosError(err) && err.response) {
                return rejectWithValue(err.response.data.message || 'Failed to fetch tracking details.');
            }
            return rejectWithValue('An unknown error occurred.');
        }
    }
);

const trackingSlice = createSlice({
    name: 'tracking',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchTrackingsByNumbers.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(fetchTrackingsByNumbers.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.trackings = action.payload;
            })
            .addCase(fetchTrackingsByNumbers.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.payload ?? 'Failed to fetch';
            });
    },
});

export const selectTracking = (state: RootState) => state.tracking;

export default trackingSlice.reducer; 